import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Calendar, Clock, Users, RefreshCw, Plus,
  Pause, Square, Layers, Stethoscope,
  Activity, ArrowRight, CalendarOff,
  CheckCircle2, Info, XCircle, Sparkles, Gauge
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { buildApiUrl } from '../../services/apiHelpers';

/* ─────────────────────────────────────────────
   UTILITIES
───────────────────────────────────────────── */
const getTodayISTISO = () => {
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000;
  const utcTime = now.getTime() + now.getTimezoneOffset() * 60 * 1000;
  const istTime = new Date(utcTime + istOffset);
  const y = istTime.getFullYear();
  const m = String(istTime.getMonth() + 1).padStart(2, "0");
  const d = String(istTime.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatIST = (date, options = {}) => {
  try {
    return new Date(date).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", ...options });
  } catch { return String(date); }
};

const fmt = t => {
  if (!t) return "--:--";
  const [hours, minutes] = String(t).split(":").map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return String(t);
  return `${hours % 12 || 12}:${String(minutes).padStart(2, "0")} ${hours >= 12 ? "PM" : "AM"}`;
};

/* total bookable slots for a session, derived from start/end/slotDuration */
const totalSlotsFor = (session) => {
  const { startTime, endTime, slotDuration } = session;
  const duration = Number(slotDuration);
  if (!startTime || !endTime || !Number.isFinite(duration) || duration <= 0) return null;
  const toMin = (time) => {
    const match = String(time).match(/^(\d{1,2}):(\d{2})/);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    return hours < 24 && minutes < 60 ? hours * 60 + minutes : null;
  };
  const start = toMin(startTime);
  const end = toMin(endTime);
  if (start === null || end === null) return null;
  let diff = end - start;
  if (diff <= 0) diff += 1440;
  return Math.max(0, Math.floor(diff / duration));
};

/* color tone for a booking-fill percentage */
const capacityTone = (pct) => {
  if (pct >= 95) return { fill: "linear-gradient(90deg,var(--rose),#c8564d)", text: "var(--rose)" };
  if (pct >= 70) return { fill: "linear-gradient(90deg,var(--amber),#c97d1f)", text: "var(--amber)" };
  return { fill: "linear-gradient(90deg,var(--blue),var(--sky))", text: "var(--blue-dark)" };
};

const CTA_LABEL = { RUNNING: "Manage live session", PAUSED: "Resume session", ENDED: "View summary", NOT_STARTED: "Open session", CANCELLED: "Session cancelled" };

/* ─────────────────────────────────────────────
   GLOBAL STYLES — premium blue design system
   (shared palette/type with the rest of the OPD suite)
───────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Newsreader:ital,wght@0,500;0,600;0,700;1,500;1,600&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  .opd-root {
    --paper:#f1f3fa;--card:#fff;--ink:#0a1226;--muted:#5c6883;--faint:#8a9ab0;
    --line:#e4e8f4;--line-2:#d2daf0;
    --blue:#1c46d4;--blue-dark:#0a1f52;--blue-light:#3f5fe0;--blue-soft:#eaeffc;--blue-border:#c9d4f9;
    --sky:#2f79e0;--sky-soft:#eaf3ff;
    --slate:#57657a;--slate-soft:#eef1f6;
    --green:#2c8a5a;--green-soft:#eff8f2;--green-border:#c7e6d4;
    --amber:#b06a0c;--amber-soft:#fdf5e9;--amber-border:#f3dbae;
    --rose:#af433c;--rose-soft:#faeceb;--rose-border:#f0c7c2;
    --radius-sm:10px;--radius-md:14px;--radius-lg:18px;--radius-xl:24px;
    --shadow-sm:0 1px 3px rgba(10,18,38,.06),0 1px 2px rgba(10,18,38,.04);
    --shadow-md:0 12px 32px rgba(10,18,38,.10);
    --shadow-lg:0 28px 70px rgba(10,18,38,.20);
    --font-body:'Plus Jakarta Sans',-apple-system,BlinkMacSystemFont,sans-serif;
    --font-disp:'Newsreader',Georgia,serif;
    --font-mono:'JetBrains Mono',ui-monospace,monospace;
    font-family: var(--font-body);
    min-height: 100vh; background: var(--paper); color: var(--ink);
    -webkit-font-smoothing: antialiased; position: relative;
  }
  .opd-root em{font-style:italic;color:var(--blue-light)}
  .opd-content{position:relative;z-index:1;max-width:1320px;margin:0 auto;padding:28px 24px 0;display:flex;flex-direction:column;min-height:100vh}
  .opd-body{flex:1 1 auto}

  @keyframes fadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:translateY(0)}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes shimmer{0%{background-position:200% center}100%{background-position:-200% center}}
  @keyframes ripple{0%{transform:scale(.8);opacity:1}100%{transform:scale(2.2);opacity:0}}
  @keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}}
  @keyframes toastIn{from{transform:translateX(120%);opacity:0}to{transform:translateX(0);opacity:1}}

  .opd-spin{animation:spin .8s linear infinite}

  /* header */
  .opd-hd-row{display:flex;justify-content:space-between;align-items:flex-start;gap:20px;flex-wrap:wrap;margin-bottom:26px}
  .opd-eyebrow{display:inline-flex;align-items:center;gap:7px;padding:5px 13px;border-radius:999px;margin-bottom:12px;
    background:linear-gradient(135deg,var(--blue-soft),#eef1fd);border:1px solid var(--blue-border)}
  .opd-eyebrow span{font-family:var(--font-mono);font-size:10.5px;font-weight:700;color:var(--blue-dark);letter-spacing:.08em;text-transform:uppercase}
  .opd-hd-row h1{font-family:var(--font-disp);font-weight:600;font-size:clamp(24px,2.6vw,31px);letter-spacing:-.01em;color:var(--ink);margin:0}
  .opd-hd-sub{display:flex;align-items:center;gap:8px;margin-top:9px;flex-wrap:wrap;font-size:13px;color:var(--muted)}
  .opd-hd-sub b{color:var(--ink);font-weight:600}
  .opd-hd-controls{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
  .opd-date-wrap{display:flex;align-items:center;gap:8px;padding:0 14px;height:46px;border-radius:var(--radius-sm);background:#fff;border:1.5px solid var(--line-2);transition:border-color .15s,box-shadow .15s}
  .opd-date-wrap:focus-within{border-color:var(--blue-light);box-shadow:0 0 0 4px rgba(63,95,224,.14)}
  .opd-date-wrap svg{color:var(--blue);flex:none}
  .opd-date-wrap input{border:none;outline:none;background:transparent;font-family:var(--font-mono);font-size:13.5px;font-weight:600;color:var(--ink);min-width:130px;color-scheme:light}
  .opd-icon-btn{width:46px;height:46px;border-radius:var(--radius-sm);background:#fff;border:1.5px solid var(--line-2);display:grid;place-items:center;color:var(--muted);transition:all .15s;flex:none}
  .opd-icon-btn:hover{border-color:var(--blue-light);color:var(--blue);background:var(--blue-soft)}

  /* stat pills */
  .opd-stats{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:24px}
  .opd-pill{display:inline-flex;align-items:center;gap:6px;padding:7px 14px;border-radius:999px;font-family:var(--font-mono);font-size:12px;font-weight:600}

  /* leave banner */
  .opd-banner{display:flex;gap:12px;align-items:flex-start;padding:16px 18px;border-radius:var(--radius-lg);margin-bottom:22px;
    background:var(--amber-soft);border:1.5px solid var(--amber-border);animation:fadeUp .4s ease both}
  .opd-banner-icon{width:36px;height:36px;border-radius:11px;background:#f7e4bd;border:1px solid var(--amber-border);display:grid;place-items:center;color:var(--amber);flex:none}
  .opd-banner strong{display:block;font-size:14px;color:#8a5a0a;margin-bottom:2px}
  .opd-banner span{font-size:13px;color:#8a5a0a;opacity:.85}

  /* session grid + card */
  .opd-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));gap:18px}
  .opd-session{position:relative;background:#fff;border:1.5px solid var(--blue-border);border-radius:var(--radius-xl);overflow:hidden;cursor:pointer;
    box-shadow:var(--shadow-sm);transition:transform .25s cubic-bezier(.34,1.56,.64,1),box-shadow .25s,border-color .25s;animation:fadeUp .45s cubic-bezier(.16,1,.3,1) both}
  .opd-session:hover{transform:translateY(-4px);box-shadow:var(--shadow-md);border-color:var(--blue-light)}
    .opd-session.cancelled{cursor:default;opacity:.78}
    .opd-session.cancelled:hover{transform:none;box-shadow:var(--shadow-sm)}
  .opd-session.live{border-color:var(--green-border)}
  .opd-session-top{height:3px;background:linear-gradient(90deg,var(--blue),var(--blue-light),var(--sky))}
  .opd-session.live .opd-session-top{background:linear-gradient(90deg,var(--green),#3fa76e,var(--blue))}
  .opd-session-body{padding:22px}
  .opd-session-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;margin-bottom:16px}
  .opd-session-id{display:flex;align-items:center;gap:12px;min-width:0}
  .opd-session-icon{width:46px;height:46px;border-radius:14px;background:var(--blue-soft);border:1px solid var(--blue-border);display:grid;place-items:center;color:var(--blue);flex:none}
  .opd-session.live .opd-session-icon{background:var(--green-soft);border-color:var(--green-border);color:var(--green)}
  .opd-session-title{font-family:var(--font-disp);font-style:italic;font-weight:600;font-size:15.5px}
  .opd-session-code{font-family:var(--font-mono);font-size:10.5px;color:var(--muted);margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
  .opd-status-badge{display:inline-flex;align-items:center;gap:6px;padding:5px 11px;border-radius:999px;font-family:var(--font-mono);font-size:10.5px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;flex:none}
  .opd-status-RUNNING{background:var(--green-soft);border:1px solid var(--green-border);color:var(--green)}
  .opd-status-PAUSED{background:var(--amber-soft);border:1px solid var(--amber-border);color:var(--amber)}
  .opd-status-ENDED{background:var(--rose-soft);border:1px solid var(--rose-border);color:var(--rose)}
  .opd-status-CANCELLED{background:var(--rose-soft);border:1px solid var(--rose-border);color:var(--rose)}
  .opd-status-NOT_STARTED{background:var(--slate-soft);border:1px solid var(--line-2);color:var(--slate)}
  .opd-live-dot{width:7px;height:7px;border-radius:50%;background:var(--green);position:relative}
  .opd-live-dot::after{content:"";position:absolute;inset:-4px;border-radius:50%;border:1.5px solid rgba(44,138,90,.45);animation:ripple 2s ease-out infinite}
  .opd-divider{height:1px;margin:0 0 14px;background:linear-gradient(90deg,transparent,var(--blue-border),transparent)}

  /* time line + capacity progress (replaces the old 2x2 info grid) */
  .opd-time-line{display:flex;align-items:center;gap:7px;font-size:13px;color:var(--ink);font-weight:600;margin-bottom:16px;flex-wrap:wrap}
  .opd-time-line svg{color:var(--blue);flex:none}
  .opd-time-line .sep{color:var(--faint);font-weight:400}
  .opd-time-line .slotnote{color:var(--muted);font-weight:500;font-family:var(--font-mono);font-size:11.5px}
  .opd-capacity{margin-bottom:2px}
  .opd-progress-row{display:flex;align-items:baseline;justify-content:space-between;margin-bottom:7px;gap:8px}
  .opd-progress-row .lbl2{font-size:12px;font-weight:700;color:var(--ink);display:flex;align-items:center;gap:6px}
  .opd-progress-row .lbl2 svg{color:var(--muted);flex:none}
  .opd-progress-row .pct{font-family:var(--font-mono);font-size:13px;font-weight:700;flex:none}
  .opd-progress-track{height:9px;border-radius:999px;background:var(--slate-soft);border:1px solid var(--line);overflow:hidden}
  .opd-progress-fill{height:100%;border-radius:999px;transition:width .6s cubic-bezier(.22,.9,.3,1);position:relative}
  .opd-progress-fill::after{content:"";position:absolute;inset:0;background:linear-gradient(90deg,rgba(255,255,255,.35),rgba(255,255,255,0))}
  .opd-progress-caption{margin-top:7px;font-size:11.5px;color:var(--muted)}

  .opd-cta{display:flex;align-items:center;justify-content:space-between;margin-top:14px;padding:12px 15px;border-radius:12px;background:var(--blue-soft);border:1px solid var(--blue-border);transition:all .2s}
  .opd-session.live .opd-cta{background:var(--green-soft);border-color:var(--green-border)}
  .opd-cta span{font-size:12.5px;font-weight:700;color:var(--blue-dark)}
  .opd-session.live .opd-cta span{color:var(--green)}
  .opd-cta .arrow{width:26px;height:26px;border-radius:999px;background:#fff;border:1px solid var(--blue-border);display:grid;place-items:center;color:var(--blue);flex:none}
  .opd-session.live .opd-cta .arrow{border-color:var(--green-border);color:var(--green)}

  /* skeleton */
  .opd-skeleton{height:280px;border-radius:var(--radius-xl);border:1px solid var(--line);
    background:linear-gradient(90deg,var(--paper) 25%,#fff 50%,var(--paper) 75%);background-size:400% 100%;animation:shimmer 1.8s ease infinite}

  /* empty state */
  .opd-empty{background:#fff;border:1px solid var(--blue-border);border-radius:var(--radius-xl);padding:64px 32px;text-align:center;box-shadow:var(--shadow-sm);animation:fadeUp .5s ease both}
  .opd-empty-icon{width:76px;height:76px;border-radius:22px;background:var(--blue-soft);border:1px solid var(--blue-border);display:grid;place-items:center;margin:0 auto 22px;animation:float 3s ease-in-out infinite;color:var(--blue)}
  .opd-empty h3{font-family:var(--font-disp);font-style:italic;font-weight:600;font-size:19px;margin:0 0 8px}
  .opd-empty p{font-size:13.5px;color:var(--muted);max-width:340px;margin:0 auto;line-height:1.6}

  /* sticky footer */
  .opd-footer-space{height:88px}
  .opd-footer{position:fixed;left:0;right:0;bottom:0;z-index:60;background:rgba(255,255,255,.9);backdrop-filter:blur(12px);
    border-top:1px solid var(--line);box-shadow:0 -10px 26px rgba(10,18,38,.06);padding:14px max(24px, calc((100vw - 1320px)/2))}
  .opd-footer-inner{max-width:1320px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap}
  .opd-footer-left{display:flex;align-items:center;gap:8px;color:var(--muted);font-size:12px}
  .opd-footer-left b{color:var(--ink)}
  .opd-footer-right{display:flex;gap:10px;flex-wrap:wrap}
  .opd-btn{height:44px;display:inline-flex;align-items:center;gap:8px;border-radius:999px;padding:0 20px;font-size:13.5px;font-weight:700;border:1.5px solid transparent;transition:all .15s}
  .opd-btn-primary{background:var(--blue);color:#fff;border-color:var(--blue);box-shadow:0 6px 18px rgba(28,70,212,.28)}
  .opd-btn-primary:hover{background:var(--blue-dark);transform:translateY(-1px)}
  .opd-btn-neutral{background:#fff;color:var(--ink);border-color:var(--line-2)}
  .opd-btn-neutral:hover{background:var(--slate-soft)}

  /* toasts */
  .opd-toast-stack{position:fixed;right:20px;bottom:104px;z-index:500;display:flex;flex-direction:column;gap:10px;align-items:flex-end;pointer-events:none}
  .opd-toast{display:flex;align-items:center;gap:10px;padding:13px 16px;border-radius:14px;background:#0d1a3a;color:#eaf0ff;font-size:13px;font-weight:600;
    box-shadow:var(--shadow-lg);max-width:320px;animation:toastIn .3s cubic-bezier(.2,.8,.2,1) both}
  .opd-toast.success{border-left:3px solid var(--green)}
  .opd-toast.info{border-left:3px solid var(--sky)}
  .opd-toast.error{border-left:3px solid var(--rose)}
  .opd-toast svg{flex:none}

  @media (prefers-reduced-motion: reduce){
    .opd-session,.opd-skeleton,.opd-live-dot,.opd-spin,.opd-empty,.opd-empty-icon,.opd-banner,.opd-toast{animation:none !important}
  }

  @media (max-width: 768px) {
    .opd-content{padding:22px 18px 0}
    .opd-hd-row{flex-direction:column;align-items:stretch;gap:18px;margin-bottom:26px}
    .opd-hd-controls{width:100%}
    .opd-date-wrap{flex:1 1 auto}
    .opd-grid{grid-template-columns:1fr}
    .opd-stats{gap:6px}
    .opd-footer{padding:12px 14px}
    .opd-footer-inner{flex-direction:column;align-items:stretch}
    .opd-footer-right{justify-content:stretch}
    .opd-footer-right .opd-btn{flex:1;justify-content:center}
  }
  @media (max-width: 480px) {
    .opd-content{padding:18px 14px 0}
    .opd-hd-row h1{font-size:21px}
    .opd-session-body{padding:18px 16px}
    .opd-date-wrap{padding:0 12px}
    .opd-date-wrap input{min-width:0;width:100%;font-size:13px}
    .opd-empty{padding:52px 22px}
    .opd-pill{font-size:11px;padding:6px 11px}
  }
  @media (min-width: 1500px) {
    .opd-grid{grid-template-columns:repeat(3,1fr)}
  }
`;

/* ─────────────────────────────────────────────
   STATUS CONFIG
───────────────────────────────────────────── */
const STATUS = {
  RUNNING:     { label: "Live",      icon: Activity },
  PAUSED:      { label: "Paused",    icon: Pause    },
  ENDED:       { label: "Ended",     icon: Square   },
  CANCELLED:   { label: "Cancelled", icon: CalendarOff },
  NOT_STARTED: { label: "Scheduled", icon: Clock    },
};

/* ─────────────────────────────────────────────
   STATUS BADGE
───────────────────────────────────────────── */
function StatusBadge({ status }) {
  const s = STATUS[status] || STATUS.NOT_STARTED;
  const isLive = status === "RUNNING";
  return (
    <div className={`opd-status-badge opd-status-${status in STATUS ? status : "NOT_STARTED"}`}>
      {isLive ? <span className="opd-live-dot" /> : <span style={{ width: 6, height: 6, borderRadius: "50%", background: "currentColor" }} />}
      {s.label}
    </div>
  );
}

/* ─────────────────────────────────────────────
   TOAST STACK
───────────────────────────────────────────── */
function ToastStack({ toasts }) {
  return (
    <div className="opd-toast-stack">
      {toasts.map((t) => (
        <div key={t.id} className={`opd-toast ${t.kind}`}>
          {t.kind === "success" && <CheckCircle2 size={16} />}
          {t.kind === "info" && <Info size={16} />}
          {t.kind === "error" && <XCircle size={16} />}
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────
   SESSION CARD — now with booking-capacity progress
───────────────────────────────────────────── */
function SessionCard({ session, onClick, index }) {
  const status = session.opdStatus || "NOT_STARTED";
  const isLive = status === "RUNNING";
  const isCancelled = status === "CANCELLED";
  const timeStr = `${fmt(session.startTime)} – ${fmt(session.endTime)}`;

  const capacity = totalSlotsFor(session);
  const booked = session.totalBookings;
  const pct = capacity > 0 ? Math.min(100, Math.round((booked / capacity) * 100)) : capacity === 0 ? 0 : null;
  const remaining = capacity === null ? null : Math.max(0, capacity - booked);
  const tone = capacityTone(pct ?? 0);
  const ctaLabel = CTA_LABEL[status] || "Open session";
  const caption = capacity === null || capacity === 0 ? "Capacity unavailable" : remaining === 0 ? "Fully booked — no slots left" : `${remaining} slot${remaining !== 1 ? "s" : ""} remaining`;

  return (
    <div
      className={`opd-session${isLive ? " live" : ""}${isCancelled ? " cancelled" : ""}`}
      style={{ animationDelay: `${index * 0.08}s` }}
      onClick={isCancelled ? undefined : onClick}
    >
      <div className="opd-session-top" />
      <div className="opd-session-body">
        <div className="opd-session-head">
          <div className="opd-session-id">
            <span className="opd-session-icon"><Stethoscope size={20} strokeWidth={1.75} /></span>
            <div style={{ minWidth: 0 }}>
              <div className="opd-session-title">{session.sessionOrder ? `Session ${session.sessionOrder}` : "OPD Session"}</div>
              <div className="opd-session-code">{session.scheduleId}</div>
            </div>
          </div>
          <StatusBadge status={status} />
        </div>
        <div className="opd-divider" />

        <div className="opd-time-line">
          <Clock size={14} />
          {timeStr} <span className="sep">·</span> <span className="slotnote">{session.slotDuration ? `${session.slotDuration} min slots` : "—"}</span>
        </div>

        <div className="opd-capacity">
          <div className="opd-progress-row">
            <span className="lbl2"><Users size={12} strokeWidth={2.3} />{capacity === null ? `${booked} booked` : `${booked} of ${capacity} booked`}</span>
            <span className="pct" style={{ color: tone.text }}>{pct === null ? "—" : `${pct}%`}</span>
          </div>
          {pct !== null && <div className="opd-progress-track"><div className="opd-progress-fill" style={{ width: `${pct}%`, background: tone.fill }} /></div>}
          <div className="opd-progress-caption">{caption}</div>
        </div>

        <div className="opd-cta">
          <span>{ctaLabel}</span>
          <span className="arrow"><ArrowRight size={13} strokeWidth={2.5} /></span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function DoctorOpdManager() {
  const { doctorId } = useParams();
  const navigate     = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const today        = getTodayISTISO();
  const initialDate  = searchParams.get("date") || today;

  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [sessions,     setSessions]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [loadError,    setLoadError]    = useState("");
  const [doctorName,   setDoctorName]   = useState("");
  const [leaveState,   setLeaveState]   = useState({ onLeave: false, leaveReason: "" });
  const [lastSynced,   setLastSynced]   = useState(null);
  const [toasts,       setToasts]       = useState([]);

  const { auth } = useAuth();
  const clinicCode = auth?.clinic?.code;
  const clinicId   = auth?.clinic?.id;

  const toastTimers = useRef({});
  const pushToast = useCallback((kind, message) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setToasts((prev) => [...prev, { id, kind, message }]);
    toastTimers.current[id] = setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
      delete toastTimers.current[id];
    }, 3200);
  }, []);
  useEffect(() => () => { Object.values(toastTimers.current).forEach(clearTimeout); }, []);

  // Inject global styles
  useEffect(() => {
    const id = "opd-manager-styles";
    if (document.getElementById(id)) return;
    const el = document.createElement("style");
    el.id = id; el.textContent = STYLES;
    document.head.appendChild(el);
    return () => document.getElementById(id)?.remove();
  }, []);

  useEffect(() => {
    if (!doctorId || !clinicId) {
      setDoctorName("");
      return undefined;
    }

    let cancelled = false;
    fetch(buildApiUrl(`doctors/${doctorId}`), {
      headers: { "X-Clinic-Id": String(clinicId) },
      credentials: "include",
    })
      .then(async (response) => {
        if (!response.ok) throw new Error("Unable to load doctor details");
        return response.json();
      })
      .then((doctor) => {
        if (!cancelled) setDoctorName(doctor.doctor_name || doctor.doctorName || "");
      })
      .catch(() => {
        if (!cancelled) setDoctorName("");
      });

    return () => { cancelled = true; };
  }, [doctorId, clinicId]);

  const fetchSessions = useCallback(async ({ announce = false } = {}) => {
    if (!doctorId || !clinicId) { setSessions([]); setLoading(false); return; }
    setLoading(true);
    setLoadError("");
    try {
      const [sessionsRes, leaveRes] = await Promise.all([
        fetch(buildApiUrl(`slots/doctor-sessions/${encodeURIComponent(doctorId)}/today?sessionDate=${encodeURIComponent(selectedDate)}`), {
          headers: { "Content-Type": "application/json", "X-Clinic-Id": clinicId },
        }),
        fetch(buildApiUrl(`slots/leave?doctorId=${encodeURIComponent(doctorId)}&date=${encodeURIComponent(selectedDate)}`), {
          headers: { "X-Clinic-Id": String(clinicId) },
        }).catch(() => null),
      ]);
      if (!sessionsRes.ok) throw new Error("Unable to load sessions");
      const data = await sessionsRes.json();
      if (data.status !== "success" || !Array.isArray(data.sessions)) throw new Error("Unable to load sessions");
      const leaveData = leaveRes?.ok ? await leaveRes.json() : { onLeave: false, leaveReason: "" };

      setLeaveState(leaveData?.onLeave
        ? { onLeave: true, leaveReason: leaveData.leaveReason || "" }
        : { onLeave: false, leaveReason: "" });

      setSessions(data.sessions.map((session) => ({
        scheduleId: session.scheduleId || `SESSION-${session.sessionOrder}`,
        startTime: session.startTime,
        endTime: session.endTime,
        opdStatus: session.opdStatus || "NOT_STARTED",
        totalBookings: Number.isFinite(Number(session.totalBookings)) ? Number(session.totalBookings) : 0,
        slotDuration: session.slotDuration ?? null,
        sessionOrder: session.sessionOrder,
        isCustom: session.isCustom,
      })));
      setLastSynced(new Date());
      if (announce) pushToast("success", "Sessions refreshed");
    } catch {
      setSessions([]);
      setLoadError("Unable to load sessions from the server. Please try refreshing.");
      if (announce) pushToast("error", "Unable to refresh sessions. Please try again.");
    }
    finally { setLoading(false); }
  }, [selectedDate, doctorId, clinicId, pushToast]);

  useEffect(() => {
    if (searchParams.get("date") !== selectedDate) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set("date", selectedDate);
        return next;
      }, { replace: true });
    }
  }, [selectedDate, searchParams, setSearchParams]);

  // silent load on mount / date change — no toast
  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleRefreshClick = () => fetchSessions({ announce: true });
  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    pushToast("info", "Loading schedule for selected date…");
  };
  const handleOpenSession = (session) => {
    pushToast("info", `Opening session ${session.scheduleId}…`);
    navigate(`/admin-dashboard/doctor-opd/${doctorId}/session/${session.scheduleId}`);
  };
  const handleNewSchedule = () => {
    // Adjust this path if your route for the "Open OPD Booking Schedule" page differs.
    navigate(`/admin-dashboard/doctor-opd/${doctorId}`);
  };

  const liveSessions  = sessions.filter(s => s.opdStatus === "RUNNING");
  const totalBookings = sessions.reduce((a, s) => a + (s.totalBookings || 0), 0);
  const sessionCapacities = sessions.map(totalSlotsFor);
  const totalCapacity = sessionCapacities.some((capacity) => capacity === null)
    ? null
    : sessionCapacities.reduce((sum, capacity) => sum + capacity, 0);
  const capacityPct = totalCapacity === null
    ? null
    : totalCapacity > 0 ? Math.round((totalBookings / totalCapacity) * 100) : 0;
  const capacityTonePill = capacityTone(capacityPct ?? 0);
  const formattedDate = formatIST(new Date(selectedDate + "T00:00:00"), {
    weekday: "short", day: "numeric", month: "long", year: "numeric",
  });

  return (
    <div className="opd-root">
      <div className="opd-content">
        <div className="opd-body">

          {/* ── Header ── */}
          <div className="opd-hd-row">
            <div>
              <div className="opd-eyebrow"><span>OPD Manager</span></div>
              <h1>Session <em>overview</em></h1>
              <div className="opd-hd-sub">
                <b>{clinicCode || "—"}</b><span>·</span><span>Dr. {doctorName || "Name unavailable"}</span>
              </div>
            </div>
            <div className="opd-hd-controls">
              <div className="opd-date-wrap">
                <Calendar size={14} strokeWidth={2} />
                <input type="date" value={selectedDate} onChange={handleDateChange} />
              </div>
              <button className="opd-icon-btn" onClick={handleRefreshClick} title="Refresh sessions">
                <RefreshCw size={16} className={loading ? "opd-spin" : ""} />
              </button>
            </div>
          </div>

          {/* ── Stats bar ── */}
          <div className="opd-stats">
            <span className="opd-pill" style={{ background: "var(--blue-soft)", color: "var(--blue-dark)", border: "1px solid var(--blue-border)" }}>
              <Layers size={11} strokeWidth={2.5} />{sessions.length} {sessions.length === 1 ? "Session" : "Sessions"}
            </span>
            <span className="opd-pill" style={{ background: "var(--green-soft)", color: "var(--green)", border: "1px solid var(--green-border)" }}>
              <Activity size={11} strokeWidth={2.5} />{liveSessions.length} Live
            </span>
            <span className="opd-pill" style={{ background: "var(--sky-soft)", color: "var(--blue)", border: "1px solid var(--blue-border)" }}>
              <Users size={11} strokeWidth={2.5} />{totalBookings} {totalBookings === 1 ? "Booking" : "Bookings"}
            </span>
            {sessions.length > 0 && (
              <span className="opd-pill" style={{ background: "#fff", color: capacityTonePill.text, border: "1px solid var(--line-2)" }}>
                <Gauge size={11} strokeWidth={2.5} />{capacityPct === null ? "Capacity unavailable" : `${capacityPct}% capacity filled`}
              </span>
            )}
            <span className="opd-pill" style={{ background: "#fff", color: "var(--muted)", border: "1px solid var(--line-2)" }}>
              <Calendar size={11} strokeWidth={2.5} />{formattedDate}
            </span>
            {leaveState.onLeave && (
              <span className="opd-pill" style={{ background: "var(--amber-soft)", color: "var(--amber)", border: "1px solid var(--amber-border)" }}>
                <CalendarOff size={11} strokeWidth={2.5} />On Leave
              </span>
            )}
          </div>

          {/* ── Leave banner ── */}
          {leaveState.onLeave && (
            <div className="opd-banner">
              <span className="opd-banner-icon"><CalendarOff size={17} strokeWidth={2} /></span>
              <div>
                <strong>Doctor is on leave — {selectedDate}</strong>
                <span>{leaveState.leaveReason || "Leave has been marked for this date."}</span>
              </div>
            </div>
          )}

          {/* ── Loading skeleton ── */}
          {loading && (
            <div className="opd-grid">
              {[1, 2, 3].map(i => <div key={i} className="opd-skeleton" style={{ animationDelay: `${i * 0.1}s` }} />)}
            </div>
          )}

          {/* ── Empty state ── */}
          {!loading && sessions.length === 0 && (
            <div className="opd-empty">
              <div className="opd-empty-icon"><Sparkles size={30} strokeWidth={1.5} /></div>
              <h3>{loadError ? "Sessions unavailable" : "No sessions found"}</h3>
              <p>
                {loadError || (leaveState.onLeave
                  ? "The doctor is on leave for this date, so no OPD sessions are available."
                  : <>No OPD sessions are scheduled for <strong>{formattedDate}</strong>. Try a different date or open a new booking schedule.</>)}
              </p>
            </div>
          )}

          {/* ── Sessions grid ── */}
          {!loading && sessions.length > 0 && (
            <div className="opd-grid">
              {sessions.map((session, i) => (
                <SessionCard
                  key={session.scheduleId}
                  session={session}
                  index={i}
                  onClick={() => handleOpenSession(session)}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── spacer so content never sits under the fixed footer ── */}
        <div className="opd-footer-space" />
      </div>

      {/* ── Sticky footer ── */}
      <div className="opd-footer">
        <div className="opd-footer-inner">
          <div className="opd-footer-left">
            <Clock size={14} />
            Last synced <b>{lastSynced ? lastSynced.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}</b>
          </div>
          <div className="opd-footer-right">
            <button className="opd-btn opd-btn-neutral" onClick={handleRefreshClick}>
              <RefreshCw size={15} className={loading ? "opd-spin" : ""} />Refresh
            </button>
            <button className="opd-btn opd-btn-primary" onClick={handleNewSchedule}>
              <Plus size={15} strokeWidth={2.2} />New schedule
            </button>
          </div>
        </div>
      </div>

      <ToastStack toasts={toasts} />
    </div>
  );
}